import { Column, Table } from "@tanstack/react-table";
import { cn } from "@theprelude/core";
import {
  Button,
  FunnelIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@theprelude/ds";
import { usePopper } from "@/hooks/use-popper";
import { Filter } from "@/lib/filter";
import { AdditionalFilter } from "../filter-form/additional-filters";
import { FilterForm, getFilterCount } from "../filter-form/filter-form";

export function TableFilterSelector({
  table,
  currentFilters,
  setFilters,
}: {
  table: Table<any>;
  currentFilters: Filter[];
  setFilters: (filters: Filter[]) => void;
}) {
  const popper = usePopper();
  const filterCount = getFilterCount(currentFilters);

  return (
    <Popover {...popper}>
      <PopoverTrigger asChild>
        <Button
          className={cn(
            "flex h-9 items-center justify-center border border-stroke-line",
            popper.open && "border-primary-foreground bg-secondary-background",
            currentFilters.length > 0 &&
              "border-primary-foreground bg-secondary-background",
          )}
        >
          <FunnelIcon className="size-4 text-secondary-foreground" />
          {currentFilters.length > 0 && (
            <div
              className={cn(
                "inline-flex items-center justify-center rounded-2xl bg-primary-background px-2 py-0 text-[0.625rem] text-primary-foreground font-normal dark:text-primary-foreground",
              )}
            >
              {filterCount}
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end">
        <TableFilterForm
          table={table}
          onClose={popper.close}
          currentFilters={currentFilters}
          setFilters={setFilters}
        />
      </PopoverContent>
    </Popover>
  );
}

type FilterableColumn<T, K = any> = Column<T, K> & {
  columnDef: Column<T, K>["columnDef"] & {
    meta: NonNullable<Column<T, K>["columnDef"]["meta"]> & {
      filter: NonNullable<
        NonNullable<Column<T, K>["columnDef"]["meta"]>["filter"]
      >;
    };
  };
};

function TableFilterForm({
  table,
  onClose,
  currentFilters,
  setFilters,
}: {
  table: Table<any>;
  onClose: () => void;
  currentFilters: Filter[];
  setFilters: (filters: Filter[]) => void;
}) {
  const additionalFilters = table.options.meta?.additionalFilters ?? [];
  const columns = table
    .getAllFlatColumns()
    .filter((column) => column.columnDef.meta?.filter);

  const availableFilters: AdditionalFilter[] = [
    ...columns
      .filter(
        (col): col is FilterableColumn<any, unknown> =>
          !!col.columnDef.meta?.filter,
      )
      .map(
        (column): AdditionalFilter => ({
          id: column.id,
          ...column.columnDef.meta.filter,
        }),
      ),
    ...additionalFilters,
  ];

  async function onSubmit(data: Filter[]) {
    setFilters(data);
    onClose();
  }

  return (
    <FilterForm
      availableFilters={availableFilters}
      defaultFilters={currentFilters}
      onSubmit={onSubmit}
      onClear={() => {
        setFilters([]);
        onClose();
      }}
      onClose={onClose}
    />
  );
}
